// hooks/useLaborCatalogReport.js
import { useCallback, useMemo, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetLaborCatalogReportQuery } from '../../../../api/endpoints/laborCatalog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';

const INITIAL_FILTER_STATE = {
    matnr: null,
    matnrMatchMode: null,
    laborDescription: null,
    laborDescriptionMatchMode: null,
    meins: null,
    meinsMatchMode: null,
    yurticiSipMkt: null,
    yurticiSipMktMatchMode: null,
    yurtdisiSipMkt: null,
    yurtdisiSipMktMatchMode: null,
};

const backendColumnMap = {
    matnr: 'Matnr',
    laborDescription: 'LaborDescription',
    meins: 'Meins',
    yurticiSipMkt: 'YurticiSipMkt',
    yurtdisiSipMkt: 'YurtdisiSipMkt',
};

const getBackendColumnName = (field) => {
    if (!field) return null;
    return backendColumnMap[field] || null;
};

const getOperatorValue = (matchMode) => {
    return matchModeToOperator[matchMode] || matchModeToOperator[FilterMatchMode.CONTAINS];
};

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    const pushFilter = (field, value, matchMode) => {
        if (value === null || value === undefined || value === '') return;
        const column = getBackendColumnName(field);
        if (!column) return;

        let filterValue = String(value);
        if (filterValue && typeof filterValue === 'string') {
            filterValue = filterValue.trim();
            
            if (field === 'yurticiSipMkt' || field === 'yurtdisiSipMkt') {
                filterValue = filterValue.replace(',', '.');
            }
        }
        if (filterValue === '') return;

        filters.push({
            column,
            operator: getOperatorValue(matchMode),
            value: filterValue
        });
    };

    pushFilter('matnr', filtersState?.matnr, filtersState?.matnrMatchMode);
    pushFilter('laborDescription', filtersState?.laborDescription, filtersState?.laborDescriptionMatchMode);
    pushFilter('meins', filtersState?.meins, filtersState?.meinsMatchMode);
    pushFilter('yurticiSipMkt', filtersState?.yurticiSipMkt, filtersState?.yurticiSipMktMatchMode);
    pushFilter('yurtdisiSipMkt', filtersState?.yurtdisiSipMkt, filtersState?.yurtdisiSipMktMatchMode);

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = getBackendColumnName(field);
    if (!column) return null;
    return [
        {
            column,
            direction: order === 1 ? 1 : 2
        }
    ];
};

export default function useLaborCatalogReport() {
    const initialLazyParams = { first: 0, rows: 10, page: 1 };
    const { th, getCurrentLanguageId } = useLanguage();  
    
    const [lazyParams, setLazyParams] = useState(initialLazyParams);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);

    const queryParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            languageId: getCurrentLanguageId(),
            filters: filters && filters.length > 0 ? filters : undefined,
            sorts: sorts && sorts.length > 0 ? sorts : undefined,
        };
    }, [lazyParams.page, lazyParams.rows, activeFilters, sortField, sortOrder, getCurrentLanguageId]);

    const exportParams = useMemo(() => {
    const filters = buildDynamicFilters(activeFilters);
    const sorts = buildDynamicSorts(sortField, sortOrder);

    return {
        languageId: getCurrentLanguageId(),
        filters: filters && filters.length > 0 ? filters : undefined,
        sorts: sorts && sorts.length > 0 ? sorts : undefined,
        compressionType: 0,
    };
}, [activeFilters, sortField, sortOrder, getCurrentLanguageId]);

    const { data, isLoading: isFetching, isError, error } = useGetLaborCatalogReportQuery(queryParams);

    const items = data ? (Array.isArray(data.items) ? data.items : []) : [];
    const totalCount = data ? (data.totalCount ?? 0) : 0;

    const handleFilterChange = useCallback((updatedFilters) => {
        setActiveFilters({
            matnr: updatedFilters.matnr || null,
            matnrMatchMode: updatedFilters.matnrMatchMode || null,
            laborDescription: updatedFilters.laborDescription || null,
            laborDescriptionMatchMode: updatedFilters.laborDescriptionMatchMode || null,
            meins: updatedFilters.meins || null,
            meinsMatchMode: updatedFilters.meinsMatchMode || null,
            yurticiSipMkt: updatedFilters.yurticiSipMkt || null,
            yurticiSipMktMatchMode: updatedFilters.yurticiSipMktMatchMode || null,
            yurtdisiSipMkt: updatedFilters.yurtdisiSipMkt || null,
            yurtdisiSipMktMatchMode: updatedFilters.yurtdisiSipMktMatchMode || null,
        });

        setLazyParams(initialLazyParams);
    }, [initialLazyParams]);

    const resetFilters = useCallback(() => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        setLazyParams(initialLazyParams);
    }, [initialLazyParams]);

    const handleSortChange = useCallback((event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;
        const backendColumn = getBackendColumnName(nextSortField);

        if (!backendColumn && nextSortOrder !== 0) {
            return;
        }

        setSortField(nextSortField);
        setSortOrder(nextSortOrder);
        setLazyParams(initialLazyParams);
    }, [initialLazyParams]);

    const onPage = useCallback((e) => {
        setLazyParams({ first: e.first, rows: e.rows, page: e.page + 1 });
    }, []);

    return {
        items,
        totalCount,
        isFetching,
        isError,
        error,
        lazyParams,
        activeFilters,
        handleFilterChange,
        resetFilters,
        onPage,
        sortField,
        sortOrder,
        onSort: handleSortChange,
        exportParams,
    };
}